import React, { useState, useEffect } from 'react';
import { Mail, Plus, Edit2, Trash2, Eye, Save, X, CheckCircle, AlertCircle, Send, Code, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_TYPES = {
  auto_reply: { label: 'Auto-Reply', color: 'bg-green-500/20 text-green-400', desc: 'Sent automatically to the visitor' },
  admin_notification: { label: 'Admin Notification', color: 'bg-blue-500/20 text-blue-400', desc: 'Sent to admin on new inquiry' },
  follow_up: { label: 'Follow-Up', color: 'bg-purple-500/20 text-purple-400', desc: 'Manual follow-up template' },
  custom: { label: 'Custom', color: 'bg-white/10 text-white/60', desc: 'Custom template' }
};

const VARIABLES = [
  { key: '{{name}}', desc: 'Visitor name' },
  { key: '{{email}}', desc: 'Visitor email' },
  { key: '{{interest}}', desc: 'Investment interest' },
  { key: '{{message}}', desc: 'Form message' },
  { key: '{{company_name}}', desc: 'Company name' },
  { key: '{{company_email}}', desc: 'Company email' },
  { key: '{{company_phone}}', desc: 'Company phone' },
  { key: '{{date}}', desc: 'Current date' }
];

const TemplateEditor = ({ template, onSave, onClose, getStoredPassword }) => {
  const [form, setForm] = useState(template || {
    name: '', subject: '', html_content: '', template_type: 'custom', enabled: true, description: ''
  });
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showVars, setShowVars] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPreview = async () => {
    if (!template?.id) {
      // For new templates, do client-side preview
      let html = form.html_content;
      const sampleData = {
        '{{name}}': 'John Smith', '{{email}}': 'john@example.com',
        '{{interest}}': 'Strategic & Creative Acquisitions', '{{message}}': "I'm interested in learning more about your investment opportunities.",
        '{{company_name}}': 'Black Star PI', '{{company_email}}': 'info@black-starpi.com',
        '{{company_phone}}': '+44 131 381 1989', '{{date}}': new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      };
      Object.entries(sampleData).forEach(([k, v]) => { html = html.replaceAll(k, v); });
      setPreviewHtml(html);
      setShowPreview(true);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${template.id}/preview?password=${getStoredPassword()}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.html_content);
        setShowPreview(true);
      }
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.html_content) {
      toast.error('Name, subject, and content are required');
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23D4AF37'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '20px'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-white/60" />
          </button>
          <h3 className="text-lg text-white">{template?.id ? 'Edit Template' : 'New Template'}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadPreview} variant="outline" className="border-white/10 text-white/70" data-testid="preview-template-btn">
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          <Button onClick={handleSave} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" disabled={saving} data-testid="save-template-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-white/50 text-sm block mb-2">Template Name *</label>
          <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="e.g., Welcome Email" />
        </div>
        <div>
          <label className="text-white/50 text-sm block mb-2">Type</label>
          <select value={form.template_type} onChange={(e) => setForm({...form, template_type: e.target.value})} className="w-full bg-[#1a2a24] border border-white/10 text-white rounded-md p-2 appearance-none cursor-pointer" style={selectStyle}>
            {Object.entries(TEMPLATE_TYPES).map(([key, { label }]) => (
              <option key={key} value={key} className="bg-[#1a2a24] text-white">{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-white/50 text-sm block mb-2">Description</label>
        <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="What is this template for?" />
      </div>

      <div>
        <label className="text-white/50 text-sm block mb-2">Email Subject *</label>
        <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="e.g., Thank you for contacting {{company_name}}, {{name}}" />
      </div>

      {/* Variable Helper */}
      <div>
        <button onClick={() => setShowVars(!showVars)} className="flex items-center gap-2 text-[#D4AF37] text-sm hover:underline" data-testid="toggle-variables-btn">
          <Code className="w-4 h-4" /> {showVars ? 'Hide' : 'Show'} Template Variables
        </button>
        {showVars && (
          <div className="mt-3 bg-white/5 border border-white/10 rounded-lg p-4">
            <p className="text-white/50 text-xs mb-3">Click a variable to copy it. Paste it into the subject or HTML content.</p>
            <div className="flex flex-wrap gap-2">
              {VARIABLES.map(v => (
                <button key={v.key} onClick={() => { navigator.clipboard.writeText(v.key); toast.success(`Copied ${v.key}`); }} className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1.5 rounded text-xs hover:bg-[#D4AF37]/20 transition-colors" title={v.desc}>
                  {v.key}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* HTML Content Editor */}
      <div>
        <label className="text-white/50 text-sm block mb-2">Email HTML Content *</label>
        <textarea
          value={form.html_content}
          onChange={(e) => setForm({...form, html_content: e.target.value})}
          className="w-full bg-white/5 border border-white/10 text-white rounded-md p-4 font-mono text-sm min-h-[300px] resize-y"
          placeholder="<div>Your email HTML content here...</div>"
          data-testid="template-html-editor"
        />
      </div>

      {/* Enable/Disable Toggle */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-white font-medium">Template Status</h4>
            <p className="text-white/40 text-sm">
              {form.template_type === 'auto_reply' && 'When enabled, auto-replies are sent to visitors on form submission.'}
              {form.template_type === 'admin_notification' && 'When enabled, admin gets notified on new form submissions.'}
              {form.template_type === 'follow_up' && 'Follow-up templates are used manually from the CRM.'}
              {form.template_type === 'custom' && 'Custom templates for ad-hoc email sending.'}
            </p>
          </div>
          <button
            onClick={() => setForm({...form, enabled: !form.enabled})}
            data-testid="template-enabled-toggle"
            className={`relative w-14 h-7 rounded-full transition-colors ${form.enabled ? 'bg-green-500' : 'bg-white/20'}`}
          >
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${form.enabled ? 'left-8' : 'left-1'}`} />
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/90" onClick={() => setShowPreview(false)} />
          <div className="relative bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-3 bg-gray-100 border-b">
              <span className="text-gray-700 font-medium text-sm">Email Preview (Sample Data)</span>
              <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-1" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      )}
    </div>
  );
};

export const EmailTemplatesTab = ({ getStoredPassword }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [followupSettings, setFollowupSettings] = useState({ enabled: false, delay_hours: 48, template_id: null });
  const [savingFollowup, setSavingFollowup] = useState(false);

  const fetchTemplates = async () => {
    try {
      const [tmplRes, fuRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/email-templates?password=${getStoredPassword()}`),
        fetch(`${API_URL}/api/admin/auto-followup-settings?password=${getStoredPassword()}`)
      ]);
      if (tmplRes.ok) setTemplates(await tmplRes.json());
      if (fuRes.ok) setFollowupSettings(await fuRes.json());
    } catch (e) {
      console.error('Failed to fetch templates');
    }
    setLoading(false);
  };

  useEffect(() => { fetchTemplates(); }, [getStoredPassword]);

  const saveTemplate = async (template) => {
    const body = {
      name: template.name, subject: template.subject, html_content: template.html_content,
      template_type: template.template_type, enabled: template.enabled, description: template.description || ''
    };
    try {
      const url = template.id
        ? `${API_URL}/api/admin/email-templates/${template.id}?password=${getStoredPassword()}`
        : `${API_URL}/api/admin/email-templates?password=${getStoredPassword()}`;
      const res = await fetch(url, {
        method: template.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(template.id ? 'Template updated' : 'Template created');
        setEditing(null);
        setCreating(false);
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch (e) {
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${id}?password=${getStoredPassword()}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Template deleted'); fetchTemplates(); }
    } catch (e) {
      toast.error('Failed to delete template');
    }
  };

  const toggleEnabled = async (template) => {
    const body = {
      name: template.name, subject: template.subject, html_content: template.html_content,
      template_type: template.template_type, enabled: !template.enabled, description: template.description || ''
    };
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${template.id}?password=${getStoredPassword()}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(template.enabled ? 'Template disabled' : 'Template enabled');
        fetchTemplates();
      }
    } catch (e) {
      toast.error('Failed to update template');
    }
  };

  if (loading) return <div className="text-center text-white/50 py-8">Loading...</div>;

  if (editing || creating) {
    return (
      <TemplateEditor
        template={editing}
        onSave={saveTemplate}
        onClose={() => { setEditing(null); setCreating(false); }}
        getStoredPassword={getStoredPassword}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">Email Templates</h3>
          <p className="text-white/40 text-sm">Manage email templates for auto-replies, notifications, and follow-ups</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Send className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#D4AF37] text-sm font-medium">How Templates Work</p>
            <p className="text-white/50 text-sm mt-1">
              <strong className="text-white/70">Auto-Reply:</strong> Automatically sent to visitors when they submit the contact form.<br />
              <strong className="text-white/70">Admin Notification:</strong> Sent to you when a new inquiry comes in.<br />
              <strong className="text-white/70">Follow-Up:</strong> Available for manual use when following up with leads.
            </p>
          </div>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="text-center text-white/50 py-8">
          <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No templates yet. Templates will be auto-created when you first open this tab.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => {
            const typeConfig = TEMPLATE_TYPES[template.template_type] || TEMPLATE_TYPES.custom;
            return (
              <div key={template.id} className={`bg-white/5 border rounded-lg p-5 ${template.enabled ? 'border-white/10' : 'border-white/5 opacity-60'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-white font-medium">{template.name}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${typeConfig.color}`}>
                        {typeConfig.label}
                      </span>
                      {template.enabled ? (
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="text-xs text-white/40 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Disabled
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-white/40 text-sm mb-2">{template.description}</p>
                    )}
                    <p className="text-white/50 text-xs font-mono truncate">
                      Subject: {template.subject}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => toggleEnabled(template)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${template.enabled ? 'bg-green-500' : 'bg-white/20'}`}
                      data-testid={`toggle-template-${template.id}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${template.enabled ? 'left-5' : 'left-0.5'}`} />
                    </button>
                    <button onClick={() => setEditing(template)} className="p-2 hover:bg-white/10 rounded" data-testid={`edit-template-${template.id}`}>
                      <Edit2 className="w-4 h-4 text-white/60" />
                    </button>
                    <button onClick={() => deleteTemplate(template.id)} className="p-2 hover:bg-red-500/20 rounded text-red-400" data-testid={`delete-template-${template.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Auto Follow-Up Settings */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6 mt-2">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h4 className="text-white font-medium">Auto Follow-Up</h4>
              <p className="text-white/40 text-sm">Automatically email leads that stay in "New" too long</p>
            </div>
          </div>
          <button
            onClick={async () => {
              const newVal = !followupSettings.enabled;
              const updated = {...followupSettings, enabled: newVal};
              setFollowupSettings(updated);
              setSavingFollowup(true);
              try {
                await fetch(`${API_URL}/api/admin/auto-followup-settings?password=${getStoredPassword()}`, {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(updated)
                });
                toast.success(newVal ? 'Auto follow-up enabled' : 'Auto follow-up disabled');
              } catch (e) { toast.error('Failed to update'); }
              setSavingFollowup(false);
            }}
            data-testid="auto-followup-toggle"
            className={`relative w-14 h-7 rounded-full transition-colors ${followupSettings.enabled ? 'bg-green-500' : 'bg-white/20'}`}
          >
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${followupSettings.enabled ? 'left-8' : 'left-1'}`} />
          </button>
        </div>
        <div className={followupSettings.enabled ? '' : 'opacity-40 pointer-events-none'}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm block mb-2">Delay (hours)</label>
              <Input
                type="number"
                value={followupSettings.delay_hours}
                onChange={(e) => setFollowupSettings({...followupSettings, delay_hours: parseInt(e.target.value) || 48})}
                className="bg-white/5 border-white/10 text-white"
                min={1}
              />
              <p className="text-white/30 text-xs mt-1">Send follow-up after this many hours in "New" status</p>
            </div>
            <div>
              <label className="text-white/50 text-sm block mb-2">Template</label>
              <select
                value={followupSettings.template_id || ''}
                onChange={(e) => setFollowupSettings({...followupSettings, template_id: e.target.value || null})}
                className="w-full bg-[#1a2a24] border border-white/10 text-white rounded-md p-2"
              >
                <option value="" className="bg-[#1a2a24]">Select template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id} className="bg-[#1a2a24]">{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <Button
            onClick={async () => {
              setSavingFollowup(true);
              try {
                await fetch(`${API_URL}/api/admin/auto-followup-settings?password=${getStoredPassword()}`, {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(followupSettings)
                });
                toast.success('Auto follow-up settings saved');
              } catch (e) { toast.error('Failed to save'); }
              setSavingFollowup(false);
            }}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E] mt-4"
            disabled={savingFollowup}
            data-testid="save-followup-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {savingFollowup ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};
